import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, Card, CardContent, CircularProgress } from '@mui/material';
import { TrendingUp, Article, AttachMoney, People } from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../components/AuthProvider';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';

interface Stats {
  contentCount: number;
  totalEarnings: number;
  partnerBrands: number;
}

export default function CreatorDashboard() {
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [stats, setStats] = useState<Stats>({ contentCount: 0, totalEarnings: 0, partnerBrands: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const [contentsRes, earningsRes, brandsRes] = await Promise.all([
          supabase.from('contents').select('id', { count: 'exact', head: true }).eq('creator_id', user.id),
          supabase.from('affiliate_earnings').select('amount').eq('creator_id', user.id),
          supabase.from('affiliate_partnerships').select('id', { count: 'exact', head: true }).eq('creator_id', user.id).eq('status', 'active'),
        ]);

        const totalEarnings = (earningsRes.data ?? []).reduce((sum: number, r: any) => sum + (r.amount || 0), 0);

        setStats({
          contentCount: contentsRes.count ?? 0,
          totalEarnings,
          partnerBrands: brandsRes.count ?? 0,
        });
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const statCards = [
    { label: '업로드 컨텐츠', value: stats.contentCount, icon: <Article sx={{ fontSize: 32, color: '#7c3aed' }} />, unit: '개' },
    { label: '총 수익', value: stats.totalEarnings.toLocaleString(), icon: <AttachMoney sx={{ fontSize: 32, color: '#10b981' }} />, unit: '원' },
    { label: '파트너 브랜드', value: stats.partnerBrands, icon: <People sx={{ fontSize: 32, color: '#3b82f6' }} />, unit: '개' },
  ];

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} mb={3} color={isDarkMode ? '#fff' : '#111'}>
        크리에이터 대시보드
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {statCards.map(card => (
            <Grid item xs={12} sm={4} key={card.label}>
              <Card elevation={0} sx={{ ...styles.paper, borderRadius: 3 }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 3 }}>
                  {card.icon}
                  <Box>
                    <Typography variant="body2" color={isDarkMode ? '#aaa' : '#666'}>
                      {card.label}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color={isDarkMode ? '#fff' : '#111'}>
                      {card.value}
                      <Typography component="span" variant="body2" ml={0.5} color={isDarkMode ? '#aaa' : '#666'}>
                        {card.unit}
                      </Typography>
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Paper elevation={0} sx={{ ...styles.paper, borderRadius: 3, p: 4, textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 48, color: isDarkMode ? '#555' : '#ddd', mb: 2 }} />
              <Typography variant="h6" fontWeight={600} color={isDarkMode ? '#aaa' : '#888'}>
                컨텐츠를 업로드하고 브랜드와 파트너십을 시작하세요
              </Typography>
              <Typography variant="body2" color={isDarkMode ? '#666' : '#bbb'} mt={1}>
                좌측 메뉴에서 컨텐츠 업로드 또는 마켓플레이스에서 브랜드를 찾아보세요.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
